import React, { createContext, useContext, useState } from 'react';

import { useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import { useStoreContext } from 'features/store';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { IGetUserFavoriteQuery, useHydrateCartMutation } from 'generated/graphql-gateway';
import { hydrateAndReturnEntries } from 'hooks/hydrateEntries';
import { routes } from 'navigation/routing';
import { useServiceModeContext } from 'state/service-mode';
import { remappedCartForBackEnd } from 'utils/cart';
import logger from 'utils/logger';
import { getCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { mapBackendToFrontend } from 'utils/reorder';
import { useMemoAll } from 'utils/use-memo-all';

interface IEditFavoriteParams {
  cartEntries: ILegacyCartEntry[];
}

interface IEditFavoriteOneMenuAdapterParams {
  minimalCartEntries: ICartEntry[];
}

export interface IFavoritesContext {
  editFavorite: (options: IEditFavoriteParams) => void;
  editFavoriteOneMenuAdapter: (params: IEditFavoriteOneMenuAdapterParams) => void;
  currentCartEntry: ILegacyCartEntry | undefined;
  isPendingSave: boolean;
  cancelEditing: () => void;
  editName: (entry: any) => void;
  editEntry: (entry: any) => void;
  upsertEntry: (newFavorite: any) => void;
  removeEntry: (lineId: string) => void;
  setCurrentEditingFavorite: (newFavorite?: IGetUserFavoriteQuery['userFavorite'] | null) => void;
  tempFavorite: IGetUserFavoriteQuery['userFavorite'] | null;
  shouldRefetchFavorites: boolean;
  setShouldRefetchFavorites: React.Dispatch<React.SetStateAction<boolean>>;
  setFavorite: (newFavorite?: IGetUserFavoriteQuery['userFavorite'] | null) => void;
}

// @TODO: This does not work as intended. We should infer the cartEntry type from the generated favorite fragment
// export type FavoriteEntries = NonNullable<IGetUserFavoriteQuery['userFavorite']['entries']>
// export type FavoriteEntry = FavoriteEntries extends (infer U)[] ? U : never;

export const FavoritesContext = createContext({} as IFavoritesContext);
export const useFavoritesContext = () => useContext(FavoritesContext);

export const FavoritesProvider: React.FC<React.PropsWithChildren<IBaseProps>> = ({ children }) => {
  const [tempFavorite, setTempFavorite] = useState<IFavoritesContext['tempFavorite']>(null);
  const [isPendingSave, setIsPendingSave] = useState<boolean>(false);
  const [currentCartEntry, setCurrentCartEntry] = useState<ILegacyCartEntry>();
  const [shouldRefetchFavorites, setShouldRefetchFavorites] = useState<boolean>(false);
  const router = useRouter();

  const [hydrateCart] = useHydrateCartMutation();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();

  const editFavorite = ({ cartEntries }: IEditFavoriteParams) => {
    if (!cartEntries) {
      return;
    }

    const backendEntries = remappedCartForBackEnd(cartEntries);
    backendEntries.forEach(upsertEntry);

    router.push(`${routes.favorites}/${tempFavorite?.favoriteId}/edit`);
  };

  const editFavoriteOneMenuAdapter = async ({
    minimalCartEntries,
  }: IEditFavoriteOneMenuAdapterParams) => {
    // For One Menu, we need to hydrate the cart entries to get the proper data for the favorites
    // TODO: [BKPE-23002] Once we stop using legacy menu, we should investigate how to send minimalCartEntries
    // directly to the backend

    try {
      const hydratedCartEntries = await hydrateAndReturnEntries({
        cartEntries: minimalCartEntries,
        storeId,
        serviceMode,
        hydrateCart,
      });

      editFavorite({ cartEntries: hydratedCartEntries });
    } catch (error) {
      logger.error({ error, message: 'Error hydrating cart entries for favorites' });
    }
  };

  /**
   * Check if the favoriteId is the same or not.
   *
   * If the favoriteId is different :
   *  -(call `cancelEditing`):
   *    - reset isPendingSave to false
   *    - change tempFavorite to null since we are starting over
   *  - update the activeEditingFavoriteId
   */
  const setCurrentEditingFavorite = (
    newFavorite?: IGetUserFavoriteQuery['userFavorite'] | null
  ) => {
    if (
      !newFavorite ||
      (tempFavorite !== null && tempFavorite.favoriteId === newFavorite.favoriteId)
    ) {
      return;
    }
    cancelEditing();
    return setTempFavorite(newFavorite);
  };

  const editName = (newName: string) => {
    setTempFavorite(
      currentTempFavorite =>
        ({
          ...currentTempFavorite,
          name: newName,
        } as IFavoritesContext['tempFavorite'])
    );
    setIsPendingSave(true);
  };

  const editEntry = (entry: any) => {
    setCurrentCartEntry({
      ...mapBackendToFrontend(entry),
      // Make sure to maintain the cartId when converting
      cartId: entry.lineId,
    });

    router.push(
      `${routes.menu}/${entry.type.toLowerCase()}/${entry.sanityId}?isEdit=true&cartIdEditing=${
        entry.lineId
      }&isFavorite=true`
    );
  };

  // @TODO: Figure out the type for the entries
  const upsertEntry = (newEntry: any) => {
    setTempFavorite(currentTempFavorite => {
      const currentEntries = currentTempFavorite?.entries ?? [];
      const existingEntryIndex = currentEntries.findIndex(
        entry => entry?.lineId === newEntry?.lineId
      );

      // replace it in its original position to maintain order
      // otherwise, append the entry to the tempFavorite.entries
      const entries: typeof currentEntries =
        existingEntryIndex !== -1
          ? currentEntries.map((entry, index) => (index === existingEntryIndex ? newEntry : entry))
          : [...currentEntries, newEntry];

      return {
        ...currentTempFavorite,
        ref: getFavoriteRef({
          entries,
        }),
        entries,
      } as IFavoritesContext['tempFavorite'];
    });

    setIsPendingSave(true);
  };

  const removeEntry = (lineId: string) => {
    setTempFavorite(currentTempFavorite => {
      const entries = currentTempFavorite?.entries?.filter(
        currentEntry => currentEntry?.lineId !== lineId
      );
      return {
        ...currentTempFavorite,
        ref: getFavoriteRef({
          entries,
        }),
        entries,
      } as IFavoritesContext['tempFavorite'];
    });

    setIsPendingSave(true);
  };

  const cancelEditing = () => {
    setIsPendingSave(false);
    setTempFavorite(null);
  };

  const getFavoriteRef = (favorite?: Partial<IGetUserFavoriteQuery['userFavorite']> | null) => {
    let ref = null;
    if (favorite?.entries?.length === 1) {
      ref = getCartEntryRef((favorite?.entries[0] as unknown) as ILegacyCartEntry);
    }
    return ref;
  };

  const value = useMemoAll({
    editFavorite,
    editFavoriteOneMenuAdapter,
    currentCartEntry,
    cancelEditing,
    editName,
    editEntry,
    isPendingSave,
    removeEntry,
    upsertEntry,
    setCurrentEditingFavorite,
    tempFavorite,
    shouldRefetchFavorites,
    setShouldRefetchFavorites,
    setFavorite: setTempFavorite as any, //`as any` as a substitute for previous ts-ignore
  });

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};
